import { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { ContentCopy, CheckCircleOutline } from '@mui/icons-material';
import DOMPurify from 'dompurify';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { materialDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { nightOwl } from 'react-syntax-highlighter/dist/esm/styles/prism';

import tabEngine from '../../TabEngine/TabEngine';
import rkLinkifier from './RkLinkifier';
import sourceTreeFormatter from './sourceTree/SourceTreeFormatter';
import SourceTreeBlock from './sourceTree/SourceTreeBlock';
import indexBlockFormatter from './indexBlock/IndexBlockFormatter';
import IndexBlock from './indexBlock/IndexBlock';
import searchQueryEngine from '../../YourSearchTab/Core/SearchQueryEngine';
import styleCache from '../../../../BodyEditorWidget/Core/StyleCache';
import userSettingsEngine from '../../../../controllers/UserSettings/UserSettingsEngine';
import '../../../../theme/JernyBodyContent.css';

/**
 * Reformats a raw (often single-line) C# snippet so each statement gets its own line,
 * without breaking auto-property accessor blocks like `{ get; set; }` or `{ get; private set; }`.
 *
 * Approach: protect every accessor block behind a placeholder token first, run the original
 * "split on every ;" pass, then restore the placeholders. A direct regex lookahead on ';'
 * was tried first but breaks — `;\s*` greedily eats the space before 'set;', forcing the
 * engine to backtrack to a zero-width whitespace match, which re-inserts the newline before
 * that space instead of skipping it. Placeholder swap sidesteps the backtracking entirely.
 *
 * @param {string} raw - raw code block content
 * @returns {string} normalized code with one statement per line
 */
function normalizeCsharp(raw) {
    const accessorBlocks = [];

    // Matches { get; set; } / { get; } / { get; private set; } etc. — whitespace-insensitive.
    const protectedRaw = raw.replace(/\{\s*get;(?:\s*(?:private|protected|internal)?\s*set;)?\s*\}/g, (block) => {
        accessorBlocks.push(block.replace(/\s+/g, ' ').trim());
        return `__ACCESSOR_${accessorBlocks.length - 1}__`;
    });

    const split = protectedRaw.replace(/;\s*/g, ';\n').replace(/\)\s*{/g, ') {');

    return split.replace(/__ACCESSOR_(\d+)__/g, (_, i) => accessorBlocks[Number(i)]);
}

/**
 * MarkdownRenderer — renders entry.body as markdown, plain text, JSON, or sanitized HTML.
 * Extracted from ContentPanel — handles syntax highlighting, external links, and RK navigation.
 * @param {object} props
 * @param {object} props.entry    - journal entry
 * @param {string} props.viewMode - 'md', 'plain', 'json', or 'html'
 * @param {(rk:string)=>void} [props.onRkClick] - optional override, default navigates via search tab
 */
export default function MarkdownRenderer({ entry, viewMode, onRkClick }) {
    /**
     * Custom markdown components — syntax highlighting, external links, RK navigation.
     */
    const markdownComponents = {
        /**
         * Handles all links.
         * #rk- links navigate to a note via search tab.
         * All other links open in a new tab.
         */
        a({ href, children }) {
            if (rkLinkifier.isRkHref(href)) {
                const rk = rkLinkifier.rkFromHref(href);

                /**
                 * Navigates to the note with this RK.
                 */
                function handleRkClick(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    if (onRkClick) onRkClick(rk);
                    else rkLinkifier.navigateTo(rk);
                }

                return (
                    <a href="#" onClick={handleRkClick} style={{ color: 'var(--j-accent)', cursor: 'pointer' }}>
                        {children}
                    </a>
                );
            }

            return (
                <a href={href} target="_blank" rel="noopener noreferrer">
                    {children}
                </a>
            );
        },

        /**
         * Renders fenced code blocks with syntax highlighting and copy button.
         * Falls back to inline code for non-fenced blocks.
         */
        code({ node, inline, className, children }) {
            const match = /language-(\w+)/.exec(className || '');

            if (!inline && match) {
                const raw = String(children).replace(/\n$/, '');
                const normalized = match[1] === 'csharp' ? normalizeCsharp(raw) : raw;

                const [copied, setCopied] = useState(false);

                const handleCopyCode = () => {
                    navigator.clipboard.writeText(normalized);
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                };

                return (
                    <Box sx={{ position: 'relative' }}>
                        <IconButton
                            size="small"
                            onClick={handleCopyCode}
                            sx={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                color: copied ? '#4cceac' : '#294766',
                                '&:hover': { color: 'var(--j-accent)' },
                                zIndex: 1,
                            }}
                        >
                            {copied ? <CheckCircleOutline fontSize="small" /> : <ContentCopy fontSize="small" />}
                        </IconButton>
                        <SyntaxHighlighter
                            style={atomDark}
                            language={match[1]}
                            customStyle={{ background: '#0b1e2e', border: '0.5px solid #294766', borderRadius: '4px', scrollbarWidth: 'thin', scrollbarColor: 'var(--jx-border) transparent' }}
                        >
                            {normalized}
                        </SyntaxHighlighter>
                    </Box>
                );
            }

            return <code className={className}>{children}</code>;
        },

        /** Renders tree-diagram paragraphs (from SourceTreeFormatter) as boxed rows. */
        sourcetreeblock(props) {
            const rows = JSON.parse(props['data-rows'] || '[]');
            return <SourceTreeBlock rows={rows} />;
        },

        /** Renders "Name | RK | Description" index-table paragraphs (from IndexBlockFormatter). */
        indexblock(props) {
            const rows = JSON.parse(props['data-rows'] || '[]');
            return <IndexBlock rows={rows} />;
        },
    };

    // HTML view — sanitize via DOMPurify, render raw HTML with style note CSS injected as sibling <style> tag
    if (viewMode === 'html') {
        const styleTitle = userSettingsEngine.resolve('bodyEditor.styleNote');
        const css = styleCache.getByTitle(styleTitle)?.body ?? '';
        const clean = DOMPurify.sanitize(entry.body, {
            // Allow style tag for injected theme CSS
            ALLOWED_TAGS: [
                'style',
                'ul', 'ol', 'li',
                'b', 'strong', 'i', 'em',
                'code', 'pre',
                'span', 'div', 'p', 'br',
                'h1', 'h2', 'h3', 'h4',
                'a', 'img', 'hr',
            ],
            // width: Tiptap writes <img width="300px"> — without this DOMPurify strips the attribute
            // and image resize set in BodyEditor is lost in ContentPanel.
            ALLOWED_ATTR: ['style', 'href', 'target', 'rel', 'src', 'alt', 'class', 'width'],
            // ADD_ATTR is required for custom data-* attributes — ALLOWED_ATTR alone does not preserve them
            ADD_ATTR: ['data-rk'],
        });

        /**
         * Handles clicks inside HTML view.
         * If the clicked element (or its closest ancestor) has data-rk,
         * navigate to that note via search tab.
         */
        const handleHtmlClick = (e) => {
            const el = e.target.closest('[data-rk]');
            if (!el) return;
            e.preventDefault();
            e.stopPropagation();
            const rk = el.getAttribute('data-rk');
            if (!rk) return;
            const index = tabEngine.getIndexById('search');
            if (index !== -1) tabEngine.setTab(index);
            searchQueryEngine.setQuery(rk);
        };

        return (
            <>
                <style>{css}</style>
                <Box
                    className="jerny-body-content"
                    sx={{
                        color: 'var(--j-text)',
                        fontSize: 13,
                        '& [data-rk]': { color: 'var(--j-accent)', cursor: 'pointer', textDecoration: 'underline' },
                    }}
                    onClick={handleHtmlClick}
                    dangerouslySetInnerHTML={{ __html: clean }}
                />
            </>
        );
    }

    // JSON view — pretty-printed syntax highlighted JSON
    // CHANGED — try/catch: body kan geen geldige JSON zijn bij snelle entry wissel (viewMode nog niet gereset)
    if (viewMode === 'json') {
        let pretty;
        try {
            pretty = JSON.stringify(JSON.parse(entry.body), null, 2);
        } catch {
            // body is geen geldige JSON — val terug naar plain tekst
            return (
                <Typography variant="body2" sx={{ color: 'var(--j-text)', whiteSpace: 'pre-wrap', fontSize: 13 }}>
                    {entry.body}
                </Typography>
            );
        }
        return (
            <SyntaxHighlighter
                style={atomDark}
                language="json"
                customStyle={{ background: '#0b1e2e', border: '0.5px solid #294766', borderRadius: '4px', fontSize: 12, scrollbarWidth: 'thin', scrollbarColor: 'var(--jx-border) transparent' }}
            >
                {pretty}
            </SyntaxHighlighter>
        );
    }

    if (viewMode === 'plain') {
        return (
            <Typography variant="body2" sx={{ color: 'var(--j-text)', whiteSpace: 'pre-wrap', fontSize: 13 }}>
                {entry.body}
            </Typography>
        );
    }

    // Escapes a backtick immediately followed by a digit (C# generic-arity marker, e.g. IFoo`1)
    // so remark never pairs two stray backticks into one bogus inline-code span across lines.
    const safeBody = entry.body.replace(/`(?=\d)/g, '\\`');

    return (
        <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm, rkLinkifier.createRemarkPlugin(), sourceTreeFormatter.createRemarkPlugin(), indexBlockFormatter.createRemarkPlugin()]}>
            {safeBody}
        </ReactMarkdown>
    );
}
